import { createClient } from '@supabase/supabase-js';
import { Database } from '../types';

const STORAGE_KEY_URL = 'supabase_url';
const STORAGE_KEY_KEY = 'supabase_anon_key';

let supabaseInstance: ReturnType<typeof createClient<Database>> | null = null;
let currentUrl: string = '';
let currentKey: string = '';

const getStoredUrl = () => {
  return localStorage.getItem(STORAGE_KEY_URL) || import.meta.env.VITE_SUPABASE_URL || '';
};

const getStoredKey = () => {
  return localStorage.getItem(STORAGE_KEY_KEY) || import.meta.env.VITE_SUPABASE_ANON_KEY || '';
};

const isValidConfig = (url: string, key: string): boolean => {
  return !!(
    url &&
    key &&
    !url.includes('your-supabase-url-here') &&
    !key.includes('your-supabase-anon-key-here') &&
    url.startsWith('https://') &&
    url.includes('.supabase.co')
  );
};

export const updateSupabaseCredentials = (url: string, key: string) => {
  if (isValidConfig(url, key)) {
    localStorage.setItem(STORAGE_KEY_URL, url);
    localStorage.setItem(STORAGE_KEY_KEY, key);
    initializeSupabaseClient();
  } else {
    console.error('❌ Invalid Supabase configuration');
  }
};

const initializeSupabaseClient = () => {
  const url = getStoredUrl();
  const key = getStoredKey();

  if (!isValidConfig(url, key)) {
    console.warn('⚠️ Supabase not configured. Please add valid credentials.');
    supabaseInstance = null;
    currentUrl = '';
    currentKey = '';
    return;
  }

  if (url === currentUrl && key === currentKey) {
    return;
  }

  try {
    supabaseInstance = createClient<Database>(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
    currentUrl = url;
    currentKey = key;
    console.log('✅ Supabase connected successfully');
  } catch (error) {
    console.error('❌ Supabase connection error:', error);
    supabaseInstance = null;
    currentUrl = '';
    currentKey = '';
  }
};

initializeSupabaseClient();

const createSupabaseClient = (): ReturnType<typeof createClient<Database>> | null => {
  const url = getStoredUrl();
  const key = getStoredKey();

  if (!isValidConfig(url, key)) {
    return null;
  }

  if (supabaseInstance && url === currentUrl && key === currentKey) {
    return supabaseInstance;
  }

  try {
    supabaseInstance = createClient<Database>(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
    currentUrl = url;
    currentKey = key;
    return supabaseInstance;
  } catch (error) {
    console.error('❌ Supabase client creation error:', error);
    return null;
  }
};

export const getSupabaseClient = () => {
  return createSupabaseClient();
};

export { supabaseInstance as supabase };